import { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { useProjectsStore } from '@/store/projects';
import { usePlotsStore } from '@/store/plots';
import { useToast } from '@/hooks/use-toast';

interface ProjectTitleProps {
  editable?: boolean;
  autoRename?: boolean;
}

export default function ProjectTitle({ editable = true, autoRename = true }: ProjectTitleProps) {
  const { currentProject, updateProject } = useProjectsStore();
  const { currentPlot } = usePlotsStore();
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(currentProject?.name || '');
  const [hasBeenManuallyRenamed, setHasBeenManuallyRenamed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Update title when project changes
  useEffect(() => {
    if (currentProject) {
      setTitle(currentProject.name);
    }
  }, [currentProject?.name]);

  // Auto-rename based on first plot/plan (3-5 word title from data meaning + user intent)
  useEffect(() => {
    if (!autoRename || hasBeenManuallyRenamed || !currentPlot) return;

    // Guard against overwriting manual renames
    const currentName = currentProject?.name || '';
    if (currentName && currentName !== 'My First Plot') return;

    // Derive candidate name from data meaning and last user intent if available
    const columns = (currentPlot as any)?.plotSpec?.data?.columns || {};
    const colValues = Object.values(columns as any).filter(Boolean) as string[];
    const inferredDataMeaning = colValues.slice(0, 3).join(' ');

    // Try to find last user message content from window (best-effort, optional)
    const lastUserIntent = (window as any)?.__lastUserIntent as string | undefined;

    const raw = `${inferredDataMeaning} ${lastUserIntent || ''}`
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    // Fallback to plot title without chart type words
    const fallbackTitle = (currentPlot as any)?.title || '';
    const cleanedFallback = fallbackTitle.replace(/\b(bar|line|scatter|heatmap|chart|plot)\b/gi, '').trim();

    const basis = raw || cleanedFallback || 'My Project';
    const words = basis.split(' ').filter(Boolean);
    const autoName = words.slice(0, Math.min(5, Math.max(3, words.length))).join(' ');

    if (autoName && autoName !== currentProject?.name) {
      handleSave(autoName, true);
    }
  }, [currentPlot, autoRename, hasBeenManuallyRenamed, currentProject]);

  const handleSave = async (newTitle: string, isAutoRename = false) => {
    if (!currentProject || newTitle === currentProject.name) {
      setIsEditing(false);
      return;
    }

    try {
      await updateProject(currentProject.id, { name: newTitle });
      
      if (!isAutoRename) {
        setHasBeenManuallyRenamed(true);
        toast({
          title: "Project renamed",
          description: `Project renamed to "${newTitle}"`,
        });
      }
    } catch (error) {
      toast({
        title: "Failed to rename",
        description: "Could not rename the project. Please try again.",
        variant: "destructive",
      });
      setTitle(currentProject.name); // Revert on error
    } finally {
      setIsEditing(false);
    }
  };

  const handleClick = () => {
    if (editable && !isEditing) {
      setIsEditing(true);
      setTimeout(() => {
        inputRef.current?.select();
      }, 0);
    }
  };

  const handleBlur = () => {
    handleSave(title);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave(title);
    } else if (e.key === 'Escape') {
      setTitle(currentProject?.name || '');
      setIsEditing(false);
    }
  };

  if (!currentProject) return null;

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="h-7 text-sm font-medium bg-transparent border-transparent hover:border-border focus:border-primary px-2"
        autoFocus
      />
    );
  }

  return (
    <span
      className={`font-medium text-foreground ${editable ? 'cursor-text hover:bg-muted px-2 py-1 rounded transition-colors' : ''}`}
      onClick={handleClick}
      title={editable ? 'Click to rename' : currentProject.name}
    >
      {currentProject.name}
    </span>
  );
}